import React, { useState, useRef, useEffect } from "react";
import {
	Box,
	Typography,
	Button,
	IconButton,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	CircularProgress,
	Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { BasicModal } from "../BasicModal/BasicModal";
import { useWindowSize } from "@uidotdev/usehooks";
import imageCompression from "browser-image-compression";
import {
	TRAVEL_KIT_MODAL,
	modalContentSx,
	primaryButtonSx,
	secondaryButtonSx,
	textFieldSx,
} from "./TravelKitModalStyles";

// Template24: Menú/Plan de viaje completamente editable
const Template24 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onNavigateToPage, // Función para navegar a una página específica: (pageId) => void
	pages = [], // Lista de todas las páginas del proyecto para seleccionar destino
	isViewer = false, // En el Viewer no se muestran botones de edición (añadir, editar, borrar, ordenar)
}) => {
	const pageData = page.data || {};
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	// Estados para los datos del menú (days/infoLinks o dias/extras como en Kioto/NY)
	const [days, setDays] = useState(() => {
		const raw = pageData.days || pageData.dias;
		if (raw && Array.isArray(raw) && raw.length > 0) {
			return raw;
		}
		return [];
	});

	const [infoLinks, setInfoLinks] = useState(() => {
		const raw = pageData.infoLinks || pageData.extras;
		if (raw && Array.isArray(raw) && raw.length > 0) {
			return raw;
		}
		return [];
	});

	const [editDayModalOpen, setEditDayModalOpen] = useState(false);
	const [editInfoLinkModalOpen, setEditInfoLinkModalOpen] = useState(false);
	const [editingItem, setEditingItem] = useState(null);
	const [editingType, setEditingType] = useState(null); // 'day' o 'infoLink'

	const [editTitle, setEditTitle] = useState("");
	const [editPageId, setEditPageId] = useState(null);
	const [editIcon, setEditIcon] = useState("");
	const [loadingIcon, setLoadingIcon] = useState(false);

	const iconInputRef = useRef(null);

	// Función helper para actualizar page.data
	const updatePageData = (newDays, newInfoLinks) => {
		updatePage({
			...page,
			data: {
				...pageData,
				days: newDays !== undefined ? newDays : days,
				infoLinks: newInfoLinks !== undefined ? newInfoLinks : infoLinks,
			},
		});
	};

	// Funciones para días
	const handleAddDay = () => {
		setEditingItem(null);
		setEditingType("day");
		setEditTitle("");
		setEditPageId(null);
		setEditIcon("");
		setEditDayModalOpen(true);
	};

	const handleEditDay = (day) => {
		setEditingItem(day);
		setEditingType("day");
		setEditTitle(day.title || day.titulo || "");
		setEditPageId(day.pageId || null);
		setEditIcon(day.icon || "");
		setEditDayModalOpen(true);
	};

	const handleDeleteDay = (dayId) => {
		setDays((prev) => {
			const newDays = prev.filter((d) => d.id !== dayId);
			updatePageData(newDays, undefined);
			return newDays;
		});
	};

	const handleMoveDay = (index, direction) => {
		if (direction === "up" && index > 0) {
			const newDays = [...days];
			[newDays[index - 1], newDays[index]] = [newDays[index], newDays[index - 1]];
			setDays(newDays);
			updatePageData(newDays, undefined);
		} else if (direction === "down" && index < days.length - 1) {
			const newDays = [...days];
			[newDays[index], newDays[index + 1]] = [newDays[index + 1], newDays[index]];
			setDays(newDays);
			updatePageData(newDays, undefined);
		}
	};

	const handleSaveDay = () => {
		if (editingItem) {
			// Editar día existente
			setDays((prev) => {
				const newDays = prev.map((d) =>
					d.id === editingItem.id
						? { ...d, title: editTitle, pageId: editPageId, icon: editIcon }
						: d
				);
				updatePageData(newDays, undefined);
				return newDays;
			});
		} else {
			// Añadir nuevo día
			setDays((prev) => {
				const newDays = [
					...prev,
					{
						id: Date.now(),
						title: editTitle,
						pageId: editPageId,
						icon: editIcon,
					},
				];
				updatePageData(newDays, undefined);
				return newDays;
			});
		}
		handleCloseDayModal();
	};

	const handleCloseDayModal = () => {
		setEditDayModalOpen(false);
		setEditingItem(null);
		setEditTitle("");
		setEditPageId(null);
		setEditIcon("");
	};

	// Funciones para infoLinks
	const handleAddInfoLink = () => {
		setEditingItem(null);
		setEditingType("infoLink");
		setEditTitle("");
		setEditPageId(null);
		setEditIcon("");
		setEditInfoLinkModalOpen(true);
	};

	const handleEditInfoLink = (infoLink) => {
		setEditingItem(infoLink);
		setEditingType("infoLink");
		setEditTitle(infoLink.title || "");
		setEditPageId(infoLink.pageId || null);
		setEditIcon(infoLink.icon || "");
		setEditInfoLinkModalOpen(true);
	};

	const handleDeleteInfoLink = (infoLinkId) => {
		setInfoLinks((prev) => {
			const newInfoLinks = prev.filter((il) => il.id !== infoLinkId);
			updatePageData(undefined, newInfoLinks);
			return newInfoLinks;
		});
	};

	const handleMoveInfoLink = (index, direction) => {
		if (direction === "left" && index > 0) {
			const newInfoLinks = [...infoLinks];
			[newInfoLinks[index - 1], newInfoLinks[index]] = [newInfoLinks[index], newInfoLinks[index - 1]];
			setInfoLinks(newInfoLinks);
			updatePageData(undefined, newInfoLinks);
		} else if (direction === "right" && index < infoLinks.length - 1) {
			const newInfoLinks = [...infoLinks];
			[newInfoLinks[index], newInfoLinks[index + 1]] = [newInfoLinks[index + 1], newInfoLinks[index]];
			setInfoLinks(newInfoLinks);
			updatePageData(undefined, newInfoLinks);
		}
	};

	const handleSaveInfoLink = () => {
		if (editingItem) {
			// Editar infoLink existente
			setInfoLinks((prev) => {
				const newInfoLinks = prev.map((il) =>
					il.id === editingItem.id
						? { ...il, title: editTitle, pageId: editPageId, icon: editIcon }
						: il
				);
				updatePageData(undefined, newInfoLinks);
				return newInfoLinks;
			});
		} else {
			// Añadir nuevo infoLink
			setInfoLinks((prev) => {
				const newInfoLinks = [
					...prev,
					{
						id: Date.now(),
						title: editTitle,
						pageId: editPageId,
						icon: editIcon,
					},
				];
				updatePageData(undefined, newInfoLinks);
				return newInfoLinks;
			});
		}
		handleCloseInfoLinkModal();
	};

	const handleCloseInfoLinkModal = () => {
		setEditInfoLinkModalOpen(false);
		setEditingItem(null);
		setEditTitle("");
		setEditPageId(null);
		setEditIcon("");
	};

	// Funciones para iconos
	const compressImage = async (file) => {
		setLoadingIcon(true);
		const options = {
			maxSizeMB: 0.1,
			maxWidthOrHeight: 100,
			useWebWorker: true,
		};
		try {
			const compressedFile = await imageCompression(file, options);
			return compressedFile;
		} catch (error) {
			console.error("Error al comprimir imagen:", error);
			setLoadingIcon(false);
			return null;
		}
	};

	const handleIconChange = async (event) => {
		const file = event.target.files[0];
		if (!file) return;

		try {
			const compressedFile = await compressImage(file);
			if (!compressedFile) return;

			const reader = new FileReader();
			reader.onloadend = () => {
				setEditIcon(reader.result);
				setLoadingIcon(false);
			};
			reader.onerror = () => {
				console.error("Error al leer la imagen");
				setLoadingIcon(false);
			};
			reader.readAsDataURL(compressedFile);
		} catch (error) {
			console.error("Error al comprimir imagen:", error);
			setLoadingIcon(false);
		}
	};

	const handleClickIconInput = () => {
		iconInputRef.current?.click();
	};

	// Función para obtener el nombre de una página
	const getPageName = (pageId) => {
		const targetPage = pages.find((p) => p.id === pageId);
		if (targetPage) {
			// Intentar obtener el título de la página (generalmente en texts[0].content)
			let pageTitle = "";
			if (targetPage.texts && targetPage.texts[0] && targetPage.texts[0].content) {
				// Limpiar HTML tags si existen (puede venir de ReactQuill)
				const tempDiv = document.createElement("div");
				tempDiv.innerHTML = targetPage.texts[0].content;
				pageTitle = tempDiv.textContent || tempDiv.innerText || "";
				pageTitle = pageTitle.trim();
			}

			// Si hay título y no es el texto por defecto, usarlo
			if (pageTitle && pageTitle !== "Título de la página" && pageTitle !== "Plan de viaje") {
				return pageTitle;
			}

			// Si no hay título válido, mostrar "Página X"
			const pageIndex = pages.findIndex((p) => p.id === pageId) + 1;
			return `Página ${pageIndex}`;
		}
		const pageIndex = pages.findIndex((p) => p.id === pageId) + 1;
		return `Página ${pageIndex}`;
	};

	// Función para navegar
	const handleNavigate = (pageId) => {
		if (onNavigateToPage && pageId) {
			onNavigateToPage(pageId);
		}
	};

	const headerText = page.texts?.[0]?.content || "Plan de viaje";
	const logoImage =
		page.canvasImages?.find((img) => img.id === "logo")?.imageUrl ||
		"/images/logo.png";

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				width: "100%",
				backgroundColor: backgroundColor && backgroundColor !== "transparent" && backgroundColor.toLowerCase() !== "#ffffff" ? backgroundColor : "#f7f7f7",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#5ba374",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px !important",
					fontWeight: 600,
					position: "sticky",
					top: 0,
					zIndex: 1,
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				}}
			>
				{headerText}
				<Box
					component="img"
					src={logoImage}
					alt="logo"
					sx={{
						width: isSmallScreen ? 66 : 80,
						position: "absolute",
						right: 2,
						top: 18,
						zIndex: 999999,
					}}
				/>
			</Box>

			{/* CONTENIDO */}
			<Box data-block-id="text-1" sx={{ padding: isSmallScreen ? "10px 10px" : "20px 16px" }}>
				{/* Sección Plan de viaje */}
				<Box sx={{ marginBottom: 3 }}>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 1.5 }}>
						<Typography variant="body2" sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "#64748b", fontWeight: 600 }}>
							Plan de viaje
						</Typography>
						{!isViewer && (
							<Button
								onClick={handleAddDay}
								variant="contained"
								size="small"
								startIcon={<AddIcon sx={{ fontSize: 16 }} />}
								sx={{
									background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
									textTransform: "none",
									fontWeight: 600,
									borderRadius: 2,
									padding: "6px 12px",
									boxShadow: "0 4px 16px rgba(99, 102, 241, 0.35)",
									fontSize: "0.75rem",
									"&:hover": {
										background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
										boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
										transform: "translateY(-2px)",
									},
								}}
							>
								Añadir
							</Button>
						)}
					</Box>
					<Box sx={{ display: "flex", flexDirection: "column", gap: isSmallScreen ? "10px" : "20px" }}>
						{days.length === 0 ? (
							<Box
								sx={{
									textAlign: "center",
									padding: 3,
									color: "#64748b",
									fontSize: 13,
								}}
							>
								{isViewer ? "No hay días en el plan." : 'No hay días añadidos. Haz clic en "Añadir" para crear tu plan de viaje.'}
							</Box>
						) : (
							days.map((day, index) => (
								<Box
									key={day.id ?? `day-${index}`}
									sx={{
										borderRadius: "12px",
										padding: 0,
										display: "flex",
										alignItems: "center",
										gap: isSmallScreen ? "5px" : "10px",
										position: "relative",
										minHeight: 30,
										width: isSmallScreen ? "100%" : "auto", //isSmallScreen ? "calc(50% - 5px)" : "auto",
										margin: isSmallScreen ? "auto" : undefined,
										marginLeft: isSmallScreen ? "auto" : "20px",
										cursor: day.pageId ? "pointer" : "default",
										transition: "transform 0.3s ease",
										"&:hover": {
											transform: day.pageId ? "translateY(-5px)" : "none",
											"& .box": {
												boxShadow: day.pageId ? "0 4px 12px rgba(0, 0, 0, 0.1)" : "none",
											},
										},
									}}
								>
									{/* Icono */}
									<Box
										sx={{
											width: isSmallScreen ? 35 : 45,
											height: isSmallScreen ? 35 : 45,
											position: "absolute",
											left: isSmallScreen ? 8 : -20,
											zIndex: 100,
											cursor: isViewer ? "default" : "pointer",
										}}
										onClick={(e) => {
											if (isViewer) return;
											e.stopPropagation();
											handleEditDay(day);
										}}
									>
										{(day.icon || day.image) ? (
											<Box component="img" src={day.icon || day.image} alt="icon" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
										) : (
											<Box sx={{ width: "100%", height: "100%", borderRadius: "50%", backgroundColor: "rgba(47, 127, 224, 0.1)", display: "flex", alignItems: "center", justifyContent: "center", border: "2px dashed rgba(47, 127, 224, 0.3)" }}>
												<AddIcon sx={{ fontSize: 20, color: "rgba(47, 127, 224, 0.5)" }} />
											</Box>
										)}
									</Box>

									{/* Contenedor del día */}
									<Box
										className="box"
										onClick={(e) => {
											if (e.target.closest("button") || e.target.closest(".MuiIconButton-root")) {
												return;
											}
											if (day.pageId && onNavigateToPage) {
												handleNavigate(day.pageId);
											} else if (!isViewer) {
												handleEditDay(day);
											}
										}}
										sx={{
											backgroundColor: "#fff",
											borderRadius: "12px",
											padding: isSmallScreen ? "12px" : "18px 16px",
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											gap: "10px",
											position: "relative",
											minHeight: 30,
											width: "100%",
											marginLeft: 0,
											textAlign: "left",
											height: isSmallScreen ? "100%" : "auto",
											flex: 1,
											transition: "box-shadow 0.3s ease",
											paddingLeft: isSmallScreen ? "38px" : "16px",
										}}
									>
										<Typography
											sx={{
												fontSize: isSmallScreen ? "10px !important" : "13px !important",
												fontWeight: 600,
												color: "#333",
												marginLeft: isSmallScreen ? "10px" : "20px",
												fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
											}}
										>
											{day.title || day.titulo || "Sin título"}
										</Typography>
										{!isViewer && (
											<Box
												sx={{
													display: "flex",
													gap: 0.5,
													alignItems: "center",
													position: "relative",
													zIndex: 10,
													flexShrink: 0,
													pointerEvents: "auto",
												}}
												onClick={(e) => e.stopPropagation()}
												onPointerDown={(e) => e.stopPropagation()}
											>
												{!day.pageId && (
													<Tooltip title="Sin página vinculada" arrow>
														<LinkOffIcon sx={{ fontSize: 16, color: "#ef4444" }} />
													</Tooltip>
												)}
												<IconButton size="small" onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleEditDay(day); }}>
													<EditIcon sx={{ fontSize: 16, color: "#64748b", pointerEvents: "none" }} />
												</IconButton>
												<IconButton size="small" onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleMoveDay(index, "up"); }} disabled={index === 0}>
													<ArrowUpwardIcon sx={{ fontSize: 16, color: "#64748b", pointerEvents: "none" }} />
												</IconButton>
												<IconButton size="small" onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleMoveDay(index, "down"); }} disabled={index === days.length - 1}>
													<ArrowDownwardIcon sx={{ fontSize: 16, color: "#64748b", pointerEvents: "none" }} />
												</IconButton>
												<IconButton size="small" onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDeleteDay(day.id); }}>
													<DeleteIcon sx={{ fontSize: 16, color: "#ef4444", pointerEvents: "none" }} />
												</IconButton>
											</Box>
										)}
										{isViewer && !day.pageId && (
											<Tooltip title="Sin página vinculada" arrow>
												<LinkOffIcon sx={{ fontSize: 16, color: "#ef4444" }} />
											</Tooltip>
										)}
									</Box>
								</Box>
							))
						)}
					</Box>
				</Box>

				{/* Sección Información útil */}
				<Box data-block-id="text-2">
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 1.5 }}>
						<Typography variant="body2" sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "#64748b", fontWeight: 600 }}>
							Información útil
						</Typography>
						{!isViewer && (
							<Button
								onClick={handleAddInfoLink}
								variant="contained"
								size="small"
								startIcon={<AddIcon sx={{ fontSize: 16 }} />}
								sx={{
									background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
									textTransform: "none",
									fontWeight: 600,
									borderRadius: 2,
									padding: "6px 12px",
									boxShadow: "0 4px 16px rgba(99, 102, 241, 0.35)",
									fontSize: "0.75rem",
									"&:hover": {
										background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
										boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
										transform: "translateY(-2px)",
									},
								}}
							>
								Añadir
							</Button>
						)}
					</Box>
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							flexWrap: "wrap",
							gap: "8px",
							paddingBottom: "2px",
							alignItems: "stretch",
						}}
					>
						{infoLinks.length === 0 ? (
							<Box
								sx={{
									textAlign: "center",
									padding: 3,
									color: "#64748b",
									fontSize: 13,
									width: "100%",
								}}
							>
								{isViewer ? "No hay enlaces de información." : 'No hay enlaces de información añadidos. Haz clic en "Añadir" para crear accesos rápidos.'}
							</Box>
						) : (
							infoLinks.map((extra, index) => (
								<Box
									key={extra.id ?? `extra-${index}`}
									onClick={(e) => {
										if (e.target.closest("button") || e.target.closest(".MuiIconButton-root")) {
											return;
										}
										if (extra.pageId && onNavigateToPage) {
											handleNavigate(extra.pageId);
										} else if (!isViewer) {
											handleEditInfoLink(extra);
										}
									}}
									sx={{
										backgroundColor: "#fff",
										borderRadius: "12px",
										width: "auto",
										minWidth: 120,
										maxWidth: isSmallScreen ? "none" : "100%",
										padding: isSmallScreen ? "0px 5px 2.5px 5px" : "10px 8px",
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										textAlign: "center",
										justifyContent: "center",
										flexShrink: 0,
										flex: "1",
										cursor: extra.pageId ? "pointer" : "default",
										transition: "transform 0.3s ease, box-shadow 0.3s ease",
										"&:hover": {
											transform: extra.pageId ? "translateY(-5px)" : "none",
											boxShadow: extra.pageId ? "0 4px 12px rgba(0, 0, 0, 0.1)" : "none",
										},
									}}
								>
									<Box sx={{ fontSize: "24px", marginBottom: "6px", position: "relative" }}>
										{(extra.icon || extra.image) ? (
											<Box component="img" src={extra.icon || extra.image} alt="icon" sx={{ width: 38, height: 57, objectFit: "contain" }} />
										) : (
											<Box sx={{ width: 38, height: 38, borderRadius: "50%", backgroundColor: "rgba(47, 127, 224, 0.1)", display: "flex", alignItems: "center", justifyContent: "center", border: "2px dashed rgba(47, 127, 224, 0.3)" }}>
												<AddIcon sx={{ fontSize: 20, color: "rgba(47, 127, 224, 0.5)" }} />
											</Box>
										)}
										{!extra.pageId && (
											<Tooltip title="Sin página vinculada" arrow>
												<LinkOffIcon sx={{ fontSize: 16, color: "#ef4444", position: "absolute", bottom: -5, right: -5, backgroundColor: "white", borderRadius: "50%" }} />
											</Tooltip>
										)}
									</Box>
									<Typography
										sx={{
											fontSize: isSmallScreen ? "10px !important" : "12px !important",
											fontWeight: 600,
											color: "#333",
											lineHeight: 1.3,
											marginTop: "-15px",
											fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
										}}
									>
										{extra.title || extra.titulo || "Sin título"}
									</Typography>
									{!isViewer && (
										<Box
											sx={{
												display: "flex",
												gap: 0.5,
												marginTop: 1,
												alignItems: "center",
												justifyContent: "center",
												position: "relative",
												zIndex: 10,
												pointerEvents: "auto",
											}}
											onClick={(e) => e.stopPropagation()}
											onPointerDown={(e) => e.stopPropagation()}
										>
											<IconButton size="small" onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleMoveInfoLink(index, "left"); }} disabled={index === 0} title="Mover a la izquierda">
												<ArrowBackIcon sx={{ fontSize: 16, color: "#64748b", pointerEvents: "none" }} />
											</IconButton>
											<IconButton size="small" onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleMoveInfoLink(index, "right"); }} disabled={index === infoLinks.length - 1} title="Mover a la derecha">
												<ArrowForwardIcon sx={{ fontSize: 16, color: "#64748b", pointerEvents: "none" }} />
											</IconButton>
											<IconButton size="small" onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleEditInfoLink(extra); }} title="Editar">
												<EditIcon sx={{ fontSize: 16, color: "#64748b", pointerEvents: "none" }} />
											</IconButton>
											<IconButton size="small" onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDeleteInfoLink(extra.id); }} title="Eliminar">
												<DeleteIcon sx={{ fontSize: 16, color: "#ef4444", pointerEvents: "none" }} />
											</IconButton>
										</Box>
									)}
								</Box>
							))
						)}
					</Box>
				</Box>
			</Box>

			{/* Modales de edición - mismo diseño que TravelKit (103/104) */}
			<BasicModal
				width={420}
				showModal={editDayModalOpen}
				handleClose={handleCloseDayModal}
				contentSx={modalContentSx}
				children={
					<Box sx={{ p: 3 }}>
						<Typography
							sx={{
								fontSize: "18px !important",
								fontWeight: 700,
								color: TRAVEL_KIT_MODAL.textPrimary,
								mb: 2,
								fontFamily: "'Inter', sans-serif",
							}}
						>
							{editingItem ? "Editar Día" : "Añadir Día"}
						</Typography>
						<TextField
							label="Título"
							value={editTitle}
							onChange={(e) => setEditTitle(e.target.value)}
							fullWidth
							size="small"
							margin="normal"
							slotProps={{ input: { sx: { fontSize: 15 } } }}
							sx={textFieldSx}
						/>
						<FormControl fullWidth size="small" margin="normal" sx={{ minHeight: 56, ...textFieldSx }}>
							<InputLabel id="edit-day-page-label">Página Destino</InputLabel>
							<Select
								labelId="edit-day-page-label"
								value={editPageId || ""}
								label="Página Destino"
								onChange={(e) => setEditPageId(e.target.value)}
								MenuProps={{
									disablePortal: true,
									sx: { zIndex: 11000 },
									PaperProps: { sx: { maxHeight: 280, zIndex: 11000 } },
								}}
							>
								<MenuItem value="">
									<em>Ninguna</em>
								</MenuItem>
								{pages
									.filter((p) => p.id !== page.id)
									.map((p) => (
										<MenuItem key={p.id} value={p.id}>
											{getPageName(p.id)}
										</MenuItem>
									))}
							</Select>
						</FormControl>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2, mb: 2 }}>
							<input
								type="file"
								accept="image/*"
								onChange={handleIconChange}
								style={{ display: "none" }}
								ref={iconInputRef}
							/>
							<Button
								variant="outlined"
								onClick={handleClickIconInput}
								disabled={loadingIcon}
								size="medium"
								sx={{ ...secondaryButtonSx, flexShrink: 0 }}
							>
								{loadingIcon ? <CircularProgress size={22} /> : editIcon ? "Cambiar Icono" : "Añadir Icono"}
							</Button>
							{editIcon && (
								<Box
									component="img"
									src={editIcon}
									alt="icono"
									sx={{ width: 44, height: 44, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "divider" }}
								/>
							)}
						</Box>
						<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
							<Button onClick={handleCloseDayModal} sx={secondaryButtonSx}>
								Cancelar
							</Button>
							<Button variant="contained" onClick={handleSaveDay} sx={primaryButtonSx}>
								Guardar
							</Button>
						</Box>
					</Box>
				}
			/>

			<BasicModal
				width={420}
				showModal={editInfoLinkModalOpen}
				handleClose={handleCloseInfoLinkModal}
				contentSx={modalContentSx}
				children={
					<Box sx={{ p: 3 }}>
						<Typography
							sx={{
								fontSize: "18px !important",
								fontWeight: 700,
								color: TRAVEL_KIT_MODAL.textPrimary,
								mb: 2,
								fontFamily: "'Inter', sans-serif",
							}}
						>
							{editingItem ? "Editar Enlace de Información" : "Añadir Enlace de Información"}
						</Typography>
						<TextField
							label="Título"
							value={editTitle}
							onChange={(e) => setEditTitle(e.target.value)}
							fullWidth
							size="small"
							margin="normal"
							slotProps={{ input: { sx: { fontSize: 15 } } }}
							sx={textFieldSx}
						/>
						<FormControl fullWidth size="small" margin="normal" sx={{ minHeight: 56, ...textFieldSx }}>
							<InputLabel id="edit-info-page-label">Página Destino</InputLabel>
							<Select
								labelId="edit-info-page-label"
								value={editPageId || ""}
								label="Página Destino"
								onChange={(e) => setEditPageId(e.target.value)}
								MenuProps={{
									disablePortal: true,
									sx: { zIndex: 11000 },
									PaperProps: { sx: { maxHeight: 280, zIndex: 11000 } },
								}}
							>
								<MenuItem value="">
									<em>Ninguna</em>
								</MenuItem>
								{pages
									.filter((p) => p.id !== page.id)
									.map((p) => (
										<MenuItem key={p.id} value={p.id}>
											{getPageName(p.id)}
										</MenuItem>
									))}
							</Select>
						</FormControl>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2, mb: 2 }}>
							<input
								type="file"
								accept="image/*"
								onChange={handleIconChange}
								style={{ display: "none" }}
								ref={iconInputRef}
							/>
							<Button
								variant="outlined"
								onClick={handleClickIconInput}
								disabled={loadingIcon}
								size="medium"
								sx={{ ...secondaryButtonSx, flexShrink: 0 }}
							>
								{loadingIcon ? <CircularProgress size={22} /> : editIcon ? "Cambiar Icono" : "Añadir Icono"}
							</Button>
							{editIcon && (
								<Box
									component="img"
									src={editIcon}
									alt="icono"
									sx={{ width: 44, height: 44, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "divider" }}
								/>
							)}
						</Box>
						<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
							<Button onClick={handleCloseInfoLinkModal} sx={secondaryButtonSx}>
								Cancelar
							</Button>
							<Button variant="contained" onClick={handleSaveInfoLink} sx={primaryButtonSx}>
								Guardar
							</Button>
						</Box>
					</Box>
				}
			/>
		</Box>
	);
};

Template24.thumbnail = "/images/template-1.png";

export default Template24;
